/**
 * ❤️ **A LITTLE RED HEART OVER A SHEEP THAT HAS MATED** — D130, protocol 46.
 *
 * 🎯 **haugur, 2026-09-30:** *"i think we should add a little red heart floating
 * above the head of a sheep that is already carrying a lamb or a sheep that has
 * already mated."* It answers the question the blue pulse cannot: **this one
 * will not take the turf — do not waste it.**
 *
 * 🔴 **A MESH IN THE WORLD, not HTML over it.** A nameplate is laid over the
 * canvas and has no depth, so a heart drawn that way would show through the pen
 * wall. This one is behind whatever is in front of it.
 *
 * ⚠️ **`MeshBasicMaterial`, the lead's rope's own** — a material the game
 * already compiles, no texture (`06_Traps.md`: *prefer a material that is
 * already in use*). Unlit on purpose: a sign, not a thing, readable at night.
 *
 * 🔁 **It turns slowly and bobs**, so it reads from every side without facing
 * the camera, and so it reads as a sign rather than as part of the sheep.
 */

import { DoubleSide, ExtrudeGeometry, Mesh, MeshBasicMaterial, Shape } from 'three';

/** 📏 **How wide the heart is**, metres — about a sheep's head. A dial. */
export const HEART_SIZE = 0.26;
/**
 * 📍 **Where it floats, in the sheep's own frame** — forward (+x, the way it
 * faces) over the head, and up. ⚠️ Scaled with the body, so a lamb's would sit
 * over a lamb's head.
 */
export const HEART_AT = { forward: 0.38, up: 1.22 } as const;
/** 🔁 Turns per second, radians. */
export const HEART_SPIN = 1.4;
/** 🎈 How far it bobs up and down, metres, and how fast, radians a second. */
export const HEART_BOB = 0.05;
export const HEART_BOB_RATE = 2.2;

/**
 * ❤️ **The heart's outline**, in a unit box: two lobes on top and a point at
 * the bottom, centred on (0, 0), from −0.5 to 0.5 across.
 */
export function heartOutline(): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  /* The classic parametric heart, x = 16 sin³t, y = 13 cos t − 5 cos 2t − … */
  for (let i = 0; i < 48; i++) {
    const t = (i / 48) * Math.PI * 2;
    const x = 16 * Math.sin(t) ** 3;
    const y = 13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t);
    out.push({ x: x / 34, y: (y + 2.5) / 34 });
  }
  return out;
}

/** ❤️ One heart mesh — shared geometry and material, so a hundred cost one. */
let shared: { geometry: ExtrudeGeometry; material: MeshBasicMaterial } | null = null;

export function makeHeart(): Mesh {
  if (shared === null) {
    const shape = new Shape();
    const pts = heartOutline();
    const first = pts[0];
    if (first !== undefined) shape.moveTo(first.x * HEART_SIZE, first.y * HEART_SIZE);
    for (const p of pts.slice(1)) shape.lineTo(p.x * HEART_SIZE, p.y * HEART_SIZE);
    shape.closePath();
    const geometry = new ExtrudeGeometry(shape, { depth: HEART_SIZE * 0.22, bevelEnabled: false });
    geometry.translate(0, 0, -HEART_SIZE * 0.11);
    shared = {
      geometry,
      material: new MeshBasicMaterial({ color: 0xd8242f, side: DoubleSide }),
    };
  }
  const heart = new Mesh(shared.geometry, shared.material);
  heart.name = 'heart';
  heart.position.set(HEART_AT.forward, HEART_AT.up, 0);
  return heart;
}

/** 🎈 Where the heart is at time `t` (seconds): its lift and its turn. */
export function heartMotion(t: number): { lift: number; turn: number } {
  return { lift: Math.sin(t * HEART_BOB_RATE) * HEART_BOB, turn: t * HEART_SPIN };
}
